// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\CampaignStatsPanel.jsx
// Название: Campaign Stats Panel - Full Analytics
// Версия: 1.0
// Дата: 23.01.2026

import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

const CampaignStatsPanel = ({ campaignId }) => {
  const [stats, setStats] = useState(null);
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadStats();
  }, [campaignId]);

  const loadStats = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, activationsRes] = await Promise.all([
        api.get(`/campaigns/${campaignId}/stats`),
        api.get(`/campaigns/${campaignId}/activations`)
      ]);
      setStats(statsRes.data || statsRes);
      setActivations(activationsRes.data?.activations || activationsRes.activations || []);
    } catch (err) {
      setError('Ошибка загрузки статистики');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <span>Загрузка статистики...</span>
      </div>
    );
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!stats) return null;

  const { gifts, referrals, promocodes, leads, budget } = stats;

  return (
    <div style={styles.container}>
      {/* Tabs */}
      <div style={styles.tabs}>
        {['overview', 'activations'].map(tab => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && '📊 Обзор'}
            {tab === 'activations' && '🎯 Активации'}                       
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div style={styles.content}>
          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            {/* Gifts Card */}
            <div style={styles.statCard}>
              <div style={styles.statIcon}>🎁</div>
              <div style={styles.statContent}>
                <div style={styles.statLabel}>Подарки</div>
                <div style={styles.statValue}>{gifts.claimed}/{gifts.total}</div>
                <div style={styles.statSub}>Claim rate: {gifts.claim_rate}%</div>
              </div>
            </div>

            {/* Referrals Card */}
            <div style={styles.statCard}>
              <div style={styles.statIcon}>👥</div>
              <div style={styles.statContent}>
                <div style={styles.statLabel}>Рефералы</div>
                <div style={styles.statValue}>{referrals.activated}/{referrals.total}</div>
                <div style={styles.statSub}>K-factor: {referrals.k_factor}</div>
              </div>
            </div>

            {/* Leads Card */}
            <div style={styles.statCard}>
              <div style={styles.statIcon}>🎯</div>
              <div style={styles.statContent}>
                <div style={styles.statLabel}>Лиды</div>
                <div style={styles.statValue}>{leads.total}</div>
                <div style={styles.statSub}>
                  {leads.direct.count} прямых + {leads.referral.count} реф.
                </div>
              </div>
            </div>

            {/* Commission Card */}
            <div style={styles.statCard}>
              <div style={styles.statIcon}>💶</div>
              <div style={styles.statContent}>
                <div style={styles.statLabel}>Оплачено</div>
                <div style={styles.statValue}>€{leads.total * 20}</div>
                <div style={styles.statSub}>
                  {leads.total} лидов × €20
                </div>
              </div>
            </div>
          </div>

          {/* Promocodes */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>🎟️ Промокоды</h4>
            <div style={styles.promocodesGrid}>
              <div style={styles.promocodeCard}>
                <div style={styles.promocodeType}>Main (10 DAR)</div>
                <div style={styles.promocodeStats}>
                  <span style={styles.promocodeRedeemed}>{promocodes.main.redeemed}</span>
                  <span style={styles.promocodeTotal}>/ {promocodes.main.total}</span>
                </div>
              </div>
              <div style={styles.promocodeCard}>
                <div style={styles.promocodeType}>Referral (5 DAR)</div>
                <div style={styles.promocodeStats}>
                  <span style={styles.promocodeRedeemed}>{promocodes.referral.redeemed}</span>
                  <span style={styles.promocodeTotal}>/ {promocodes.referral.total}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activations Tab */}
      {activeTab === 'activations' && (
        <div style={styles.content}>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Тип</th>
                  <th style={styles.th}>Пользователь</th>
                  <th style={styles.th}>Контакты</th>
                  <th style={styles.th}>Получил</th>
                  <th style={styles.th}>Бонус инвайтеру</th>
                  <th style={styles.th}>Дата</th>
                </tr>
              </thead>
              <tbody>
                {activations.map((act, idx) => (
                  <tr key={idx} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: act.type === 'direct' ? '#dbeafe' : '#fef3c7',
                        color: act.type === 'direct' ? '#1d4ed8' : '#92400e'
                      }}>
                        {act.type === 'direct' ? '🎁 Прямой' : '👥 Реферал'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.userName}>{act.first_name} {act.last_name}</div>
                      {act.inviter_email && (
                        <div style={styles.inviter}>от {act.inviter_name}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div>{act.email}</div>
                      <div style={styles.phone}>{act.phone}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.amount}>{parseFloat(act.amount).toFixed(0)} DAR</span>
                    </td>
                    <td style={styles.td}>
                      {act.type === 'referral' ? (
                        <span style={{color: '#8B5CF6', fontWeight: 600}}>+5 DAR → {act.inviter_name}</span>
                      ) : (
                        <span style={{color: '#9ca3af'}}>—</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {new Date(act.activated_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {activations.length === 0 && (
              <div style={styles.empty}>Пока нет активаций</div>
            )}
          </div>
        </div>
      )}

      {/* Referrals Tab */}
      {activeTab === 'referrals' && (
        <div style={styles.content}>
          <div style={styles.refStats}>
            <div style={styles.refStatItem}>
              <span style={styles.refStatLabel}>Всего создано</span>
              <span style={styles.refStatValue}>{referrals.total}</span>
            </div>
            <div style={styles.refStatItem}>
              <span style={styles.refStatLabel}>Готовы</span>
              <span style={{...styles.refStatValue, color: '#3B82F6'}}>{referrals.ready}</span>
            </div>
            <div style={styles.refStatItem}>
              <span style={styles.refStatLabel}>Активировано</span>
              <span style={{...styles.refStatValue, color: '#10B981'}}>{referrals.activated}</span>
            </div>
            <div style={styles.refStatItem}>
              <span style={styles.refStatLabel}>Истекло</span>
              <span style={{...styles.refStatValue, color: '#EF4444'}}>{referrals.expired}</span>
            </div>
          </div>
          <div style={styles.kFactor}>
            <span>Вирусный коэффициент (K-factor):</span>
            <span style={styles.kValue}>{referrals.k_factor}</span>
            <span style={styles.kDesc}>
              {parseFloat(referrals.k_factor) >= 1 ? '🚀 Вирусный рост!' : '📈 Нужно больше рефералов'}
            </span>
          </div>
        </div>
      )}

      {/* Budget Tab */}
      {activeTab === 'budget' && (
        <div style={styles.content}>
          <div style={styles.budgetSection}>
            <h4 style={styles.sectionTitle}>💰 Бюджет кампании</h4>
            
            <div style={styles.budgetBar}>
              <div 
                style={{
                  ...styles.budgetSpent,
                  width: `${(budget.spent_total / budget.max_total) * 100}%`
                }}
              />
            </div>
            <div style={styles.budgetLabels}>
              <span>Потрачено: {budget.spent_total} DAR</span>
              <span>Макс: {budget.max_total} DAR</span>
            </div>

            <div style={styles.budgetDetails}>
              <div style={styles.budgetRow}>
                <span>🎁 Прямые подарки</span>
                <span>{budget.spent_direct} / {budget.max_direct} DAR</span>
              </div>
              <div style={styles.budgetRow}>
                <span>👥 Реферальные бонусы</span>
                <span>{budget.spent_referral} / {budget.max_referral} DAR</span>
              </div>
              <div style={{...styles.budgetRow, ...styles.budgetTotal}}>
                <span>📊 Остаток</span>
                <span style={{color: '#10B981', fontWeight: 700}}>{budget.remaining} DAR</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <button style={styles.refreshBtn} onClick={loadStats}>
        🔄 Обновить данные
      </button>
    </div>
  );
};

// ============================================
// STYLES
// ============================================
const styles = {
  container: {
    background: 'white',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 40,
    color: '#6b7280'
  },
  spinner: {
    width: 24,
    height: 24,
    border: '3px solid #e5e7eb',
    borderTopColor: '#10B981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  error: {
    padding: 20,
    background: '#fef2f2',
    color: '#dc2626',
    borderRadius: 12,
    textAlign: 'center'
  },
  tabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 24,
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: 16
  },
  tab: {
    padding: '10px 20px',
    border: 'none',
    background: '#f3f4f6',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: '#6b7280',
    transition: 'all 0.2s'
  },
  tabActive: {
    background: '#10B981',
    color: 'white'
  },
  content: {
    minHeight: 200
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 24
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    background: '#f8fafc',
    borderRadius: 12,
    border: '1px solid #e5e7eb'
  },
  statIcon: {
    fontSize: 32
  },
  statContent: {
    flex: 1
  },
  statLabel: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 4
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1f2937'
  },
  statSub: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2
  },
  section: {
    marginTop: 24
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: 16
  },
  promocodesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16
  },
  promocodeCard: {
    padding: 16,
    background: '#f0fdf4',
    borderRadius: 12,
    border: '1px solid #bbf7d0'
  },
  promocodeType: {
    fontSize: 14,
    color: '#166534',
    marginBottom: 8
  },
  promocodeStats: {
    fontSize: 20,
    fontWeight: 700
  },
  promocodeRedeemed: {
    color: '#10B981'
  },
  promocodeTotal: {
    color: '#9ca3af'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    background: '#f8fafc',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    borderBottom: '1px solid #e5e7eb'
  },
  tr: {
    borderBottom: '1px solid #f3f4f6'
  },
  td: {
    padding: '12px 16px',
    fontSize: 14,
    color: '#374151'
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500
  },
  userName: {
    fontWeight: 600,
    color: '#1f2937'
  },
  inviter: {
    fontSize: 12,
    color: '#9ca3af'
  },
  phone: {
    fontSize: 12,
    color: '#9ca3af'
  },
  amount: {
    fontWeight: 600,
    color: '#10B981'
  },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: '#9ca3af'
  },
  refStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    marginBottom: 24
  },
  refStatItem: {
    textAlign: 'center',
    padding: 16,
    background: '#f8fafc',
    borderRadius: 12
  },
  refStatLabel: {
    display: 'block',
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 8
  },
  refStatValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1f2937'
  },
  kFactor: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    background: '#fef3c7',
    borderRadius: 12,
    fontSize: 14
  },
  kValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#92400e'
  },
  kDesc: {
    color: '#92400e'
  },
  budgetSection: {
    padding: 20,
    background: '#f8fafc',
    borderRadius: 12
  },
  budgetBar: {
    height: 12,
    background: '#e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 8
  },
  budgetSpent: {
    height: '100%',
    background: 'linear-gradient(90deg, #10B981, #34d399)',
    borderRadius: 6,
    transition: 'width 0.5s'
  },
  budgetLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 20
  },
  budgetDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  budgetRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 14,
    padding: '8px 0',
    borderBottom: '1px solid #e5e7eb'
  },
  budgetTotal: {
    borderBottom: 'none',
    paddingTop: 12,
    fontSize: 16,
    fontWeight: 600
  },
  refreshBtn: {
    marginTop: 24,
    padding: '12px 24px',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: '#6b7280',
    width: '100%',
    transition: 'all 0.2s'
  }
};

export default CampaignStatsPanel;
